import { useEffect, useRef, useState, memo } from "react";
import { differenceInMinutes } from "date-fns";
import { Loader2, MessageSquare, MessageSquareOff, Maximize2, Minimize2 } from "lucide-react";

import { useAuth } from "@/lib/auth-context";
import { useTarefaChat, MensagemRender } from "@/hooks/tarefas/use-tarefa-chat";
import { useTarefaVisualizacoes } from "@/hooks/tarefas/use-tarefa-visualizacoes";
import { cn } from "@/lib/utils";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

import { TarefaChatInput } from "./chat/TarefaChatInput";
import { TarefaChatMessage } from "./chat/TarefaChatMessage";
import { usePermissions } from "@/hooks/use-permissions";
import { MensagemTipoAutor, PermissaoEnum } from "@workflow/types";
import { PessoaDTO } from "@/types/pessoa";
import { TarefaVisualizacaoDTO } from "@/types/tarefa.type";
import { TarefaSystemMessage } from "./chat/TarefaSystemMessage";

interface TarefaChatCardProps {
  tarefaId: number;
  isConcluido: boolean;
  criadoPorId?: number;
  atribuidoAId?: number;
  observadoresIds?: number[];
}

// ... (ChatLoadingState, ChatEmptyState e ChatArea permanecem iguais) ...
const ChatLoadingState = () => (
  <div className="flex flex-1 items-center justify-center">
    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
  </div>
);

const ChatEmptyState = ({ isConcluido }: { isConcluido: boolean }) => (
  <div className="mx-auto flex max-w-sm flex-1 flex-col items-center justify-center space-y-4 p-8 text-center duration-500 animate-in fade-in zoom-in-95">
    <div className="relative">
      <div className="absolute -inset-1 rounded-full bg-primary/10 blur-xl" />
      <div className="relative flex h-16 w-16 items-center justify-center rounded-2xl border border-border/50 bg-muted shadow-sm">
        <MessageSquareOff className="h-8 w-8 text-muted-foreground" />
      </div>
    </div>
    <div className="space-y-1">
      <h3 className="text-base font-semibold text-foreground">Nenhuma mensagem ainda</h3>
      <p className="text-sm leading-relaxed text-muted-foreground">
        {isConcluido
          ? "Esta tarefa foi encerrada sem conversas no chat. Você ainda pode enviar uma mensagem."
          : "Use este espaço para tirar dúvidas, registrar decisões ou avisar sobre impedimentos."}
      </p>
    </div>
  </div>
);

interface ChatAreaProps {
  mensagens: MensagemRender[];
  isLoading: boolean;
  isConcluido: boolean;
  isChatDisabled: boolean;
  user: PessoaDTO;
  sendMessage: (msg: string) => void;
  isMensagemNova: (msg: MensagemRender) => boolean;
  leitores: TarefaVisualizacaoDTO[];
}

const ChatArea = memo(
  ({
    mensagens,
    isLoading,
    isConcluido,
    isChatDisabled,
    user,
    sendMessage,
    isMensagemNova,
    leitores,
  }: ChatAreaProps) => {
    const scrollRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    }, [mensagens.length, mensagens[mensagens.length - 1]?.isOptimistic]);

    return (
      <div className="relative flex h-full flex-1 flex-col overflow-hidden">
        <div
          ref={scrollRef}
          className="custom-scrollbar bg-grid-slate-50/[0.02] flex flex-1 flex-col overflow-y-auto scroll-smooth p-5"
        >
          {isLoading ? (
            <ChatLoadingState />
          ) : mensagens.length === 0 ? (
            <ChatEmptyState isConcluido={isConcluido} />
          ) : (
            // mt-auto: ancora a lista no rodapé (padrão de chat) — com poucas mensagens
            // elas ficam coladas no input em vez de flutuar no topo com um vão vazio embaixo.
            <div className="mt-auto space-y-4 pb-2">
              {mensagens.map((msg, index) => {
                if (msg.tipo === MensagemTipoAutor.SISTEMA) {
                  return <TarefaSystemMessage key={msg.id} msg={msg} />;
                }

                const prevMsg = index > 0 ? mensagens[index - 1] : null;
                const nextMsg = index < mensagens.length - 1 ? mensagens[index + 1] : null;

                const isMesmoAutor =
                  prevMsg &&
                  prevMsg.tipo !== MensagemTipoAutor.SISTEMA &&
                  prevMsg.criadoPor?.id === msg.criadoPor?.id;

                const agruparComAnterior =
                  isMesmoAutor &&
                  msg.criadoEm &&
                  prevMsg?.criadoEm &&
                  differenceInMinutes(new Date(msg.criadoEm), new Date(prevMsg.criadoEm)) < 3;

                const isMinha = Number(msg.criadoPor?.id) === Number(user?.id);
                const isUltima = index === mensagens.length - 1;

                return (
                  <TarefaChatMessage
                    key={msg.id}
                    msg={msg}
                    isMinha={isMinha}
                    agruparComAnterior={!!agruparComAnterior}
                    isUltima={isUltima}
                    nextMsg={nextMsg}
                    isNova={isMensagemNova(msg)}
                    leitores={isMinha && isUltima ? leitores : undefined}
                  />
                );
              })}
            </div>
          )}
        </div>
        <TarefaChatInput
          onSend={sendMessage}
          isChatDisabled={isChatDisabled}
          isConcluido={isConcluido}
        />
      </div>
    );
  },
);
ChatArea.displayName = "ChatArea";

// --- COMPONENTE PRINCIPAL ATUALIZADO ---
export function TarefaChatCard({
  tarefaId,
  isConcluido,
  criadoPorId,
  atribuidoAId,
  observadoresIds = [],
}: TarefaChatCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [isFullScreen, setIsFullScreen] = useState(false);

  const { user } = useAuth();
  const { isAdmin } = usePermissions();

  const isCriador = Number(user?.id) === Number(criadoPorId);
  const isAtribuido = Number(user?.id) === Number(atribuidoAId);

  const isObservador = observadoresIds.some((id) => Number(id) === Number(user?.id));

  // Mesma precedência do `Tarefa.obterTipoAutor` no backend.
  const tipoAutor = isCriador
    ? MensagemTipoAutor.CRIADOR
    : isAtribuido
      ? MensagemTipoAutor.ATRIBUIDO
      : isObservador
        ? MensagemTipoAutor.OBSERVADOR
        : isAdmin
          ? MensagemTipoAutor.ADMIN
          : null;

  // Tarefa encerrada continua aceitando mensagem normal — só o papel na tarefa restringe.
  const isChatDisabled = tipoAutor === null;

  const { mensagens, isLoading, sendMessage, isMensagemNova } = useTarefaChat(
    tarefaId,
    tipoAutor ?? MensagemTipoAutor.ATRIBUIDO,
  );
  const { visualizacoes } = useTarefaVisualizacoes(tarefaId);

  // Só os outros participantes — quem enviou nunca precisa ver a si mesmo na lista de leitura.
  const leitores = visualizacoes.filter((v) => Number(v.pessoa.id) !== Number(user?.id));

  const chatProps = {
    mensagens,
    isLoading,
    isConcluido,
    isChatDisabled,
    user,
    sendMessage,
    isMensagemNova,
    leitores,
  };

  // Função de FullScreen idêntica à do Superset
  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      cardRef.current
        ?.requestFullscreen()
        .then(() => setIsFullScreen(true))
        .catch((err) => console.error(`Erro ao tentar modo tela cheia: ${err.message}`));
    } else {
      document.exitFullscreen().then(() => setIsFullScreen(false));
    }
  };

  // Effect para sincronizar o estado caso o usuário saia do fullscreen apertando a tecla ESC
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullScreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  return (
    <Card
      ref={cardRef}
      className={cn(
        "flex flex-1 flex-col overflow-hidden border-border bg-card shadow-sm",
        // Ajusta as classes dependendo se está em fullscreen ou não para garantir que ocupe a tela toda
        isFullScreen
          ? "h-screen max-h-screen w-screen rounded-none border-none"
          : "max-h-[700px] min-h-[550px]",
      )}
    >
      <CardHeader className="shrink-0 border-b bg-muted/20 py-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-sm font-semibold text-foreground/90">
            <MessageSquare className="h-4 w-4 text-primary" /> Discussão da Tarefa
          </CardTitle>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 sm:flex">
              <div
                className={cn(
                  "h-2 w-2 animate-pulse rounded-full",
                  isChatDisabled ? "bg-zinc-400" : "bg-emerald-500",
                )}
              />
              <span className="text-xs font-medium text-muted-foreground">
                {isChatDisabled ? "Chat Inativo" : "Canal Ativo"}
              </span>
            </div>

            <div className="hidden h-4 w-px bg-border sm:block" />

            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-muted-foreground hover:bg-muted hover:text-foreground"
              onClick={toggleFullScreen}
              title={isFullScreen ? "Sair da tela cheia" : "Expandir chat"}
            >
              {isFullScreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="relative flex h-full flex-1 flex-col overflow-hidden bg-background p-0">
        <ChatArea {...chatProps} />
      </CardContent>
    </Card>
  );
}
